import { AfterViewInit, Component, EventEmitter, HostListener, Output, Renderer2 } from '@angular/core';

import { TriageUserDataService } from '../../services/triage-user-data.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-symptom-assessment-bodymap',
  imports: [TranslateModule],
  templateUrl: './symptom-assessment-bodymap.component.html',
  styleUrl: './symptom-assessment-bodymap.component.scss',
})
export class SymptomAssessmentBodymapComponent implements AfterViewInit {
  genderAge: string = '';
  viewDetail: string = 'triage-body';
  @Output() emitZoneSelected: EventEmitter<any> = new EventEmitter<any>();
  @Output() nextStepTriggered: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private triageUserDataService: TriageUserDataService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    if (this.triageUserDataService.has('age') && this.triageUserDataService.has('gender')) {
      this.initializeData();
    } else {
      console.error('Age and Gender data are missing!');
    }
  }

  onImageLoad() {
    setTimeout(() => {
      $('img[usemap]').rwdImageMaps();
    }, 0);
  }

  ngAfterViewInit(): void {
    $('img[usemap]').rwdImageMaps();
    this.makeAreasKeyboardAccessible('init');
  }

  private makeAreasKeyboardAccessible(_source: string): void {
    const areas = document.querySelectorAll<HTMLElement>('area');
    areas.forEach((area) => {
      if (area.dataset['keyboardAccessible']) {
        return;
      }

      area.dataset['keyboardAccessible'] = 'true';
      area.setAttribute('tabindex', '0');
      area.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          area.click();
        }
      });
      area.addEventListener('focus', () => this.showFocusIndicator(area));
      area.addEventListener('blur', () => this.removeFocusIndicator());
    });
  }

  private showFocusIndicator(area: HTMLElement): void {
    this.removeFocusIndicator();

    if (area.getAttribute('shape')?.toLowerCase() !== 'circle') {
      return;
    }

    const coordinates = area
      .getAttribute('coords')
      ?.split(',')
      .map((coordinate) => Number(coordinate.trim()));
    const map = area.closest('map');
    const mapName = map?.getAttribute('name');
    const image = mapName ? document.querySelector<HTMLImageElement>(`img[usemap="#${mapName}"]`) : null;

    if (!coordinates || coordinates.length !== 3 || coordinates.some(Number.isNaN) || !image) {
      return;
    }

    const [x, y, radius] = coordinates;
    const imageBounds = image.getBoundingClientRect();
    const imageContainer = image.parentElement;
    const containerBounds = imageContainer?.getBoundingClientRect();

    if (!imageContainer || !containerBounds) {
      return;
    }

    const indicator = document.createElement('span');

    indicator.className = 'body-map-focus-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    imageContainer.style.position = 'relative';
    indicator.style.position = 'absolute';
    indicator.style.left = `${imageBounds.left - containerBounds.left + x}px`;
    indicator.style.top = `${imageBounds.top - containerBounds.top + y}px`;
    indicator.style.width = `${radius * 2}px`;
    indicator.style.height = `${radius * 2}px`;
    indicator.style.transform = 'translate(-50%, -50%)';
    indicator.style.boxSizing = 'border-box';
    indicator.style.border = '3px solid #075f9b';
    indicator.style.borderRadius = '50%';
    indicator.style.backgroundColor = 'rgb(7 95 155 / 16%)';
    indicator.style.pointerEvents = 'none';
    indicator.style.zIndex = '9999';

    imageContainer.appendChild(indicator);
  }

  private removeFocusIndicator(): void {
    document.querySelector('.body-map-focus-indicator')?.remove();
  }

  initializeData(): void {
    const age = this.triageUserDataService.get('age');
    const gender = this.triageUserDataService.get('gender');

    switch (true) {
      case age === 0:
        this.genderAge = 'N';
        break;
      case age < 13:
        this.genderAge = 'B';
        break;
      default:
        this.genderAge = gender;
    }
  }

  showPartPage(bodyPart: string): void {
    this.viewDetail = bodyPart;
    setTimeout(() => this.makeAreasKeyboardAccessible('showPartPage'), 50);
  }

  showBodyPartPage(zone: string, label: string): void {
    this.triageUserDataService.set('triageData', { label, zone });
    this.emitZoneSelected.emit(zone);
    this.nextStepTriggered.emit();
  }

  showPrincipalPage(): void {
    this.hideBodyParts();
    this.viewDetail = 'triage-body';
    this.showBodyParts();
    setTimeout(() => this.makeAreasKeyboardAccessible('showPrincipalPage'), 50);
  }

  hideBodyParts(): void {
    switch (this.viewDetail) {
      case 'triage-body':
        this.setVisibility('body', false);
        break;
      case 'triage-head':
        this.setVisibility('head', false);
        break;
      case 'triage-chaist':
        this.setVisibility('chaist', false);
        break;
      case 'triage-arms':
        this.setVisibility('arms', false);
        break;
      case 'triage-back':
        this.setVisibility('back', false);
        break;
      case 'triage-stomach':
        this.setVisibility('stomach', false);
        break;
      case 'triage-pelvis':
        this.setVisibility('pelvis', false);
        break;
      case 'triage-hands':
        this.setVisibility('hands', false);
        break;
      case 'triage-legs':
        this.setVisibility('legs', false);
        break;
      case 'triage-foots':
        this.setVisibility('foots', false);
        break;
      default:
        break;
    }
  }

  showBodyParts(): void {
    switch (this.viewDetail) {
      case 'triage-body':
        this.setVisibility('body', true);
        break;
      case 'triage-head':
        this.setVisibility('head', true);
        break;
      case 'triage-chaist':
        this.setVisibility('chaist', true);
        break;
      case 'triage-arms':
        this.setVisibility('arms', true);
        break;
      case 'triage-back':
        this.setVisibility('back', true);
        break;
      case 'triage-stomach':
        this.setVisibility('stomach', true);
        break;
      case 'triage-pelvis':
        this.setVisibility('pelvis', true);
        break;
      case 'triage-hands':
        this.setVisibility('hands', true);
        break;
      case 'triage-legs':
        this.setVisibility('legs', true);
        break;
      case 'triage-foots':
        this.setVisibility('foots', true);
        break;
      default:
        break;
    }
  }

  private setVisibility(part: string, visible: boolean): void {
    const elementMap: { [key: string]: string } = {
      body: `body-${this.genderAge.toLowerCase()}`,
      head: `head-${this.genderAge.toLowerCase()}`,
      chaist: `chaist-${this.genderAge.toLowerCase()}`,
      arms: `arms-${this.genderAge.toLowerCase()}`,
      back: `back-${this.genderAge.toLowerCase()}`,
      stomach: `stomach-${this.genderAge.toLowerCase()}`,
      pelvis: `pelvis-${this.genderAge.toLowerCase()}`,
      hands: `hands-${this.genderAge.toLowerCase()}`,
      legs: `legs-${this.genderAge.toLowerCase()}`,
      foots: `foots-${this.genderAge.toLowerCase()}`,
    };

    const className = elementMap[part];
    const element = document.querySelector(`.${className}`);

    if (element) {
      if (visible) {
        this.renderer.setStyle(element, 'display', 'block');
      } else {
        this.renderer.setStyle(element, 'display', 'none');
      }
    }
  }
}
